import React, { useRef, useState } from "react";
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButton, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const helpers = [
    { name: "Firefighter", image: "firefighter.png" },
    { name: "Doctor", image: "doctor.png" },
    { name: "Police Officer", image: "policeofficer.png" },
    { name: "Chef", image: "chef.png" },
    { name: "Farmer", image: "farmer.png" },
    { name: "Construction Worker", image: "construction-worker.png" },
    { name: "Pilot", image: "pilot.png" },
    { name: "Teacher", image: "teacher.png" },
    { name: "Dentist", image: "dentist.png" }, // 🦷 Toothbrush
    { name: "Mechanic", image: "mechanic.png" }, // 🔧 Wrench
    { name: "Nurse", image: "nurse.png" }, // 💉 Syringe
    { name: "Mail Carrier", image: "mail-carrier.png" }, // 📬 Mailbag
    { name: "Scientist", image: "scientist.png" }, // 🧪 Test Tubes
    { name: "Artist", image: "artist.png" }, // 🎨 Paintbrush
    { name: "Soldier", image: "soldier.png" }, // 🎖️ Badge
    { name: "Barrister", image: "barrister.png" }, // ⚖️ Gavel
    { name: "Plumber", image: "plumber.png" }, // 🚰 Pipe Wrench
    { name: "Electrician", image: "electrician.png" }, // 🔌 Wire Cutter
    { name: "Fisherman", image: "fisherman.png" }, // 🎣 Fishing Rod
];

// Function to get 3 random incorrect options + 1 correct option
const getRandomUniqueOptions = (correctOption: { name: string }) => {
    const options = [correctOption];
    const availableOptions = helpers.filter(helper => helper.name !== correctOption.name);

    while (options.length < 4) {
        const randomIndex = Math.floor(Math.random() * availableOptions.length);
        options.push(availableOptions[randomIndex]);
        availableOptions.splice(randomIndex, 1);
    }
    return options.sort(() => Math.random() - 0.5);
};

const GuessHelpers: React.FC = () => {
    // Function to get a new question
    const getNewQuestion = () => {
        document.title = "Guess the Community Helper – Nursery Worksheet | Mama’s Guidance"; // Set page title
        const newQuestion = helpers[Math.floor(Math.random() * helpers.length)];
        return {
            question: newQuestion,
            image: newQuestion.image,
            options: getRandomUniqueOptions(newQuestion)
        };
    };

    const [{ question, image, options }, setQuizState] = useState(getNewQuestion());
    const [feedback, setFeedback] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const handleAnswer = (selected: string) => {
        if (selected === question.name) {
            setShowImage(true);
            setTimeout(() => {
                setQuizState(getNewQuestion());
                setFeedback("");
                setShowImage(false);
            }, 3000);
        } else {
            setFeedback("❌ Try Again!");
        }
    };

    const nextQuestion = () => {
        setShowImage(true);
        setTimeout(() => {
            setQuizState(getNewQuestion());
            setFeedback("");
            setShowImage(false);
        }, 3000);
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Introduce nursery kids to the essential roles of community helpers with the "Guess the Community Helper" activity on Mamas Guidance—an interactive game that helps children recognize and learn about people who support our communities, fostering awareness and respect.' />
            </Helmet>
            {!showImage ? (
                <div className="contentPart additionWord matchImage match-container">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>Guess the Community Helper</h1>

                            <p>
                                Let your little learner discover the people who make our community run smoothly! With the <strong>Guess the Community Helper</strong> worksheet, children will <strong>identify different community helpers based on simple picture or job clues</strong>. It&apos;s a great way to boost social awareness in nursery-level students.
                            </p>

                            <h2>👮‍♀️ What’s in the Worksheet?</h2>
                            <p>
                                This activity includes illustrations or hints like <strong>“Who brings letters?”</strong>, <strong>“Who drives an ambulance?”</strong>, or <strong>“Who helps us in school?”</strong>. Kids will <strong>guess the correct helper</strong> from options such as <b><em>postman, doctor, teacher, police officer, firefighter, and more</em></b>.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Recognize community helpers and their roles</li>
                                <li>Understand the importance of each profession</li>
                                <li>Improve observation and reasoning skills</li>
                                <li>Enhance vocabulary and real-world knowledge</li>
                            </ul>

                            <h2>👩‍🏫 How to Use This Worksheet</h2>
                            <ul>
                                <li>Read the question or show the picture to your child.</li>
                                <li>Discuss the clue and ask who the helper might be.</li>
                                <li>Let them circle or say the correct answer.</li>
                                <li>Extend learning by discussing where they’ve seen that helper in real life.</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/nursery/general-awareness/neighbourhood-area/guess-the-area"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Guess the Area</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/general-awareness/colors/pick-color"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Pick Color</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/general-awareness/transport-mode/guess-the-transport"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Guess the Transport</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why This Activity Matters</h2>
                            <p>
                                Teaching kids about community helpers builds <strong>appreciation, empathy, and understanding of everyday services</strong>. It helps them recognize the world around them and feel connected to society.
                            </p>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>Guess the Community Helper</h1>

                        <p>
                            Let your little learner discover the people who make our community run smoothly! With the <strong>Guess the Community Helper</strong> worksheet, children will <strong>identify different community helpers based on simple picture or job clues</strong>. It&apos;s a great way to boost social awareness in nursery-level students.
                        </p>

                        <h2>👮‍♀️ What’s in the Worksheet?</h2>
                        <p>
                            This activity includes illustrations or hints like <strong>“Who brings letters?”</strong>, <strong>“Who drives an ambulance?”</strong>, or <strong>“Who helps us in school?”</strong>. Kids will <strong>guess the correct helper</strong> from options such as <b><em>postman, doctor, teacher, police officer, firefighter, and more</em></b>.
                        </p>

                        <h2>🎯 Learning Objectives</h2>
                        <ul>
                            <li>Recognize community helpers and their roles</li>
                            <li>Understand the importance of each profession</li>
                            <li>Improve observation and reasoning skills</li>
                            <li>Enhance vocabulary and real-world knowledge</li>
                        </ul>

                        <h2>👩‍🏫 How to Use This Worksheet</h2>
                        <ul>
                            <li>Read the question or show the picture to your child.</li>
                            <li>Discuss the clue and ask who the helper might be.</li>
                            <li>Let them circle or say the correct answer.</li>
                            <li>Extend learning by discussing where they’ve seen that helper in real life.</li>
                        </ul>


                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/nursery/general-awareness/neighbourhood-area/guess-the-area"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Guess the Area</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/nursery/general-awareness/colors/pick-color"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Pick Color</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/nursery/general-awareness/transport-mode/guess-the-transport"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Guess the Transport</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🧠 Why This Activity Matters</h2>
                        <p>
                            Teaching kids about community helpers builds appreciation, empathy, and understanding of everyday services. It helps them recognize the world around them and feel connected to society.
                        </p>
                    </article>
                    <div className="mt-4">
                        <img
                            src={process.env.REACT_APP_API_URL + image}
                            alt={question.name}
                            className="season-image helper-image"
                        />
                    </div>
                    <div className="mt-4" style={{ display: "flex", flexWrap: "wrap", justifyContent: "center" }}>
                        {options.map((option, index) => (
                            <button
                                key={index}
                                className="optionButton background-blue"
                                onClick={() => handleAnswer(option.name)}
                                style={{ margin: "10px", padding: "10px", fontSize: "16px" }}
                            >
                                {option.name}
                            </button>
                        ))}
                    </div>
                    <h3>{feedback}</h3>
                    {/* <div className="btn hero-btn" onClick={nextQuestion}>Next Question</div> */}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Loading..."
                    />
                </div>
            )}
        </>
    );
};

export default GuessHelpers;
